import { parseFormNumber, roundToPrecision } from '@/helpers/finance';
import { FINANCE_ROUNDING_TOTAL_DECIMAL_PLACES } from '@/modules/finance/common/constants/rounding.constants';

import { BillDetailsItemType } from '../types';
import { calculateSalaryAdjustment, sumReconciledLineAmounts } from './salary-adjustment';

export interface BillLineAmountInput {
  entityType: BillDetailsItemType;
  quantity: string;
  unitPrice: string;
  amount: string;
}

const isReconciledLine = (entityType: BillDetailsItemType): boolean =>
  entityType === BillDetailsItemType.Project || entityType === BillDetailsItemType.TimeOffType;

const isRateBasedLine = (entityType: BillDetailsItemType): boolean =>
  isReconciledLine(entityType) || entityType === BillDetailsItemType.Overhead;

/** Returns the amount ultimately sent for one editable bill line. */
export const getBillLineAmount = (item: BillLineAmountInput): number =>
  isRateBasedLine(item.entityType)
    ? parseFormNumber(item.quantity) * parseFormNumber(item.unitPrice)
    : parseFormNumber(item.amount);

export const getMonthlySalaryAdjustment = ({
  items,
  monthlySalaryAmount,
}: {
  items: readonly BillLineAmountInput[];
  monthlySalaryAmount: string;
}): number =>
  calculateSalaryAdjustment({
    monthlySalaryAmount: parseFormNumber(monthlySalaryAmount),
    reconciledLinesTotal: sumReconciledLineAmounts(
      items
        .filter(item => isReconciledLine(item.entityType))
        .map(item => ({
          hours: parseFormNumber(item.quantity),
          rate: parseFormNumber(item.unitPrice),
        }))
    ),
  });

/** Calculates the final total, including the derived monthly-salary line when enabled. */
export const getBillTotal = ({
  items,
  monthlySalaryEnabled,
  monthlySalaryAmount,
}: {
  items: readonly BillLineAmountInput[];
  monthlySalaryEnabled: boolean;
  monthlySalaryAmount: string | null;
}): number => {
  const itemsTotal = items.reduce((sum, item) => sum + getBillLineAmount(item), 0);

  if (!monthlySalaryEnabled || !monthlySalaryAmount) {
    return roundToPrecision(itemsTotal, FINANCE_ROUNDING_TOTAL_DECIMAL_PLACES);
  }

  const monthlySalaryTotal =
    items.length === 0
      ? parseFormNumber(monthlySalaryAmount)
      : getMonthlySalaryAdjustment({ items, monthlySalaryAmount });

  return roundToPrecision(itemsTotal + monthlySalaryTotal, FINANCE_ROUNDING_TOTAL_DECIMAL_PLACES);
};
